import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  Palette as PaletteIcon,
  Inventory as ProductsIcon,
  CheckCircle as CheckIcon,
  Campaign as CampaignIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import ManualInputForm from './ManualInputForm';

const WebsitePreview = ({ 
  analysisData, 
  campaignData, 
  onCreateCampaign, 
  isCreating = false,
  onUpdateBusinessInfo,
  onUpdateCampaignData,
  selectedBusinessProfile = null,
  showProfileInfo = false
}) => {
  const [editMode, setEditMode] = React.useState(false);
  const [editData, setEditData] = React.useState(null);
  const [editingCampaignName, setEditingCampaignName] = React.useState(false);
  const [tempCampaignName, setTempCampaignName] = React.useState(campaignData?.campaignName || '');

  React.useEffect(() => {
    if (editMode) {
      setEditData({ 
        ...analysisData,
        // Ensure products is always an array for ManualInputForm
        products: Array.isArray(analysisData?.products) ? analysisData.products : []
      });
    }
  }, [editMode, analysisData]);

  React.useEffect(() => {
    setTempCampaignName(campaignData?.campaignName || '');
  }, [campaignData?.campaignName]);

  const handleEditClick = () => setEditMode(true);
  const handleCancelEdit = () => setEditMode(false);
  const handleSaveEdit = (updatedInfo) => {
    setEditMode(false);
    if (onUpdateBusinessInfo) {
      onUpdateBusinessInfo(updatedInfo);
    }
  };

  const handleCampaignNameEdit = () => {
    setEditingCampaignName(true);
  };

  const handleCampaignNameSave = () => {
    if (tempCampaignName.trim() && onUpdateCampaignData) {
      onUpdateCampaignData({ campaignName: tempCampaignName.trim() });
    }
    setEditingCampaignName(false);
  };

  const handleCampaignNameCancel = () => {
    setTempCampaignName(campaignData?.campaignName || '');
    setEditingCampaignName(false);
  };

  const handleCampaignNameKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCampaignNameSave();
    } else if (event.key === 'Escape') {
      handleCampaignNameCancel();
    }
  };

  if (!analysisData) {
    return (
      <Alert severity="info">
        No website analysis data available
      </Alert>
    );
  }

  console.log('[WebsitePreview] Received analysisData:', analysisData);
  
  const analysisItems = [
    {
      icon: <BusinessIcon />,
      label: 'Business Name',
      value: analysisData.businessName || 'Not detected',
      detected: !!analysisData.businessName
    },
    {
      icon: <DescriptionIcon />,
      label: 'Description',
      value: analysisData.description || 'Not detected',
      detected: !!analysisData.description
    },
    {
      icon: <CategoryIcon />,
      label: 'Industry',
      value: analysisData.industry || 'Not detected',
      detected: !!analysisData.industry
    },
    {
      icon: <PeopleIcon />,
      label: 'Target Audience',
      value: analysisData.targetAudience || 'General audience',
      detected: !!analysisData.targetAudience
    },
    {
      icon: <PaletteIcon />,
      label: 'Brand Tone',
      value: analysisData.brandTone || 'Professional',
      detected: !!analysisData.brandTone
    }
  ];

  const campaignGoalLabels = {
    'product_launch': 'Product/Service Launch',
    'brand_awareness': 'Brand Awareness',
    'event_promotion': 'Event Promotion',
    'sales_promotion': 'Sales/Promotion',
    'content_promotion': 'Newsletter/Content'
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Website Analysis Complete
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: showProfileInfo ? 2 : 3 }}>
        Review the extracted information and create your campaign
      </Typography>

      {/* Selected Business Profile Info */}
      {showProfileInfo && selectedBusinessProfile && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                Using Existing Business Profile: {selectedBusinessProfile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Campaign will use the brand information and assets from this profile. 
                You can edit the details below if needed.
              </Typography>
            </Box>
            <Chip
              label="Existing Profile"
              color="success"
              variant="outlined"
              size="small"
            />
          </Box>
        </Alert>
      )}

      {/* Campaign Name Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CampaignIcon color="primary" />
            <Typography variant="h6">
              Campaign Name
            </Typography>
          </Box>
          
          {editingCampaignName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                value={tempCampaignName}
                onChange={(e) => setTempCampaignName(e.target.value)}
                onKeyDown={handleCampaignNameKeyPress}
                autoFocus
                placeholder="Enter campaign name"
                variant="outlined"
                size="small"
              />
              <Button 
                variant="contained" 
                size="small" 
                onClick={handleCampaignNameSave}
                disabled={!tempCampaignName.trim()}
              >
                Save
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleCampaignNameCancel}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {campaignData?.campaignName || 'Untitled Campaign'}
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleCampaignNameEdit}
                title="Edit campaign name"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can change this name anytime after creating the campaign
          </Typography>
        </CardContent>
      </Card>

      {/* Primary Action Button - Positioned at the top for immediate visibility */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 4, 
        p: 3,
        backgroundColor: (theme) => theme.palette.primary[50],
        borderRadius: 2,
        border: '1px solid',
        borderColor: (theme) => theme.palette.primary[200]
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Ready to Create Your Campaign?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll generate 8-10 tailored social media posts for your business
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onCreateCampaign}
            disabled={isCreating || !campaignData?.campaignName?.trim()}
            sx={{ minWidth: 200, mb: 1 }}
          >
            {isCreating ? 'Creating Campaign...' : 'Create Campaign'}
          </Button>
          {!campaignData?.campaignName?.trim() && (
            <Typography variant="caption" color="error" display="block">
              Please enter a campaign name above
            </Typography>
          )}
          {isCreating && (
            <Typography variant="caption" color="text.secondary" display="block">
              Generating your personalized campaign posts...
            </Typography>
          )}
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Campaign Settings
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Website URL
                </Typography>
                <Typography variant="body2">
                  {campaignData.websiteUrl}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Campaign Goal
                </Typography>
                <Chip 
                  label={campaignGoalLabels[campaignData.campaignGoal] || campaignData.campaignGoal}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              
              {campaignData.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Additional Context
                  </Typography>
                  <Typography variant="body2">
                    {campaignData.description}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom>
                  Extracted Business Information
                </Typography>
                {!editMode && (
                  <Button size="small" variant="outlined" onClick={handleEditClick}>
                    Edit
                  </Button>
                )}
              </Box>
              {editMode ? (
                <ManualInputForm
                  onSubmit={handleSaveEdit}
                  websiteUrl={campaignData.websiteUrl}
                  initialData={editData}
                  submitLabel="Save"
                  cancelLabel="Cancel"
                  onCancel={handleCancelEdit}
                />
              ) : (
                <>
                  <List dense>
                    {analysisItems.map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.detected ? 
                            <CheckIcon color="success" fontSize="small" /> : 
                            item.icon
                          }
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.value}
                          secondaryTypographyProps={{
                            color: item.detected ? 'text.primary' : 'text.secondary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {analysisData.products && analysisData.products.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Products/Services Detected
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {analysisData.products.map((product, index) => (
                          <Chip 
                            key={index} 
                            label={product} 
                            size="small" 
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What happens next?
        </Typography>
        <Typography variant="body2">
          We'll generate 8-10 tailored social media posts optimized for Twitter, Facebook, Instagram, and LinkedIn. 
          Each post will be crafted specifically for your business and campaign goal.
        </Typography>
      </Alert>

      {/* Secondary Action Button - Available after reviewing details */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          size="medium"
          onClick={onCreateCampaign}
          disabled={isCreating || !campaignData?.campaignName?.trim()}
          sx={{ minWidth: 200 }}
        >
          {isCreating ? 'Creating Campaign...' : 'Create Campaign'}
        </Button>
      </Box>

      {isCreating && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Generating your personalized campaign posts...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WebsitePreview; 